import React, {
  useEffect,
  useRef,
  useMemo,
  useState,
  useCallback,
} from "react";
import { usePizzaEvaluationContext } from "@/context/pizza-evaluation-context";
import { useAsset } from "@/hooks/use-asset";
import { defaultHeight, defaultWidth } from "@/constants/image";
import { cn } from "@/lib/utils";
import {
  Crop,
  Flame,
  Image as ImageIcon,
  Loader2,
  Maximize2,
  ScanSearch,
  Scissors,
  Shapes,
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { ViewerTab } from "@/pizza-detail/evaluation-panel";
import { drawHoveredComponentOverlay } from "@/lib/hovered-component-overlay";
import { useLocalization } from "@/context/localization-context";
import { usePizzaSlicing } from "@/hooks/use-pizza-slicing";
import {
  SlicingBadge,
  SlicingLoupe,
  SlicingModeToggle,
  SlicingOverlay,
  SlicingPanel,
} from "@/components/evaluation/slicing-overlay";
import type { SlicingViewMode } from "@/lib/slicing";
import { usePizzaObstacles } from "@/hooks/use-pizza-obstacles";
import {
  ObstacleBadge,
  ObstacleModeToggle,
  ObstacleOverlay,
  ObstaclePanel,
} from "@/components/evaluation/obstacle-overlay";
import {
  isObstacleNok,
  obstacleRoiFrame,
  storedObstacleCheck,
  type ObstacleViewMode,
} from "@/lib/obstacles";

/** how long a pizza has to stay on screen before its obstacle check is computed on demand */
const OBSTACLE_PREVIEW_DELAY_MS = 600;

/**
 * PizzaEvaluationVisualSimple - the photo viewer of the pizza detail: one canvas with the photo cropped to
 * the pizza and the layer of the active tab (mask, doneness heatmap, slicing, obstacles) over it.
 */
interface PizzaEvaluationVisualSimpleProps {
  /** CSS length the photo may take at most in height (e.g. "100svh - 12rem"); the width follows */
  maxImageHeight?: string;
  /** a tab opened from outside (the evaluation panel's links); `n` makes a repeated request count */
  tabRequest?: { tab: ViewerTab; n: number } | null;
  onTabChange?: (tab: ViewerTab) => void;
}


const PizzaEvaluationVisualSimple = React.forwardRef<
  HTMLDivElement,
  PizzaEvaluationVisualSimpleProps & React.HTMLAttributes<HTMLDivElement>
>(({ className, maxImageHeight, tabRequest, onTabChange }, ref) => {
  const {
    pizzaImage,
    selectedPizzaId,
    paddedPizzaRoi: pizzaRoi,
    applyRoi,
    layerOpacity,
    hoveredClass,
    sauceMask,
    hoveredHeatmapImage,
    metadata,
    setApplyRoi,
    loading: evaluating,
  } = usePizzaEvaluationContext();

  const { t } = useLocalization();

  // State for active tab
  const [activeTab, setActiveTab] = useState<ViewerTab>("masked");
  useEffect(() => {
    onTabChange?.(activeTab);
  }, [activeTab, onTabChange]);
  // Slicing layer: hovered slice number (shared by the photo overlay and the area chart)
  const [hoveredSlice, setHoveredSlice] = useState<number | null>(null);
  const showSlicing = activeTab === "slicing";
  // Slicing tab: hatched overlay or the bare source photo (+ magnifier); S switches
  const [slicingMode, setSlicingMode] = useState<SlicingViewMode>("overlay");
  useEffect(() => {
    if (!showSlicing) return;
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (e.ctrlKey || e.metaKey || e.altKey || el?.closest("input, textarea, select, [contenteditable=true]")) return;
      if (e.key === "s" || e.key === "S") {
        e.preventDefault();
        setSlicingMode((m) => (m === "overlay" ? "source" : "overlay"));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showSlicing]);
  const {
    slicing,
    loading: slicingLoading,
    error: slicingError,
  } = usePizzaSlicing(selectedPizzaId, metadata?.slicing_model, showSlicing && !!metadata);

  // Obstacle layer: the masks behind the obstacle_in_pizza check (hand / paper / lid over the pizza)
  const storedObstacles = storedObstacleCheck(metadata);
  const showObstacles = activeTab === "obstacles";
  const [hoveredObstacle, setHoveredObstacle] = useState<string | null>(null);
  // Obstacle tab: masks over the photo or the bare photo with a dashed outline; M switches
  const [obstacleMode, setObstacleMode] = useState<ObstacleViewMode>("mask");
  const {
    obstacles,
    preview: obstaclesPreview,
    loading: obstaclesLoading,
    error: obstaclesError,
  } = usePizzaObstacles(
    selectedPizzaId,
    storedObstacles,
    // Evaluations made before the segmentation are checked on demand right away (not only on this tab), so
    // that an obstacle the model finds can open the layer; held back a moment while paging through pizzas.
    !!metadata && metadata.classification !== "No Pizza",
    showObstacles ? 0 : OBSTACLE_PREVIEW_DELAY_MS,
  );
  // An obstacle - in the stored verdict, or found by the on-demand check - makes this layer the default tab
  // (the mask says why the photo is invalid). The next pizza without one returns to the tab shown before.
  // A tab the user picks stays for that pizza.
  const obstacleNok = isObstacleNok(storedObstacles) || isObstacleNok(obstacles);
  const openedForObstacle = useRef(false);
  const tabBeforeObstacle = useRef<ViewerTab>("masked");
  const pickedByUser = useRef(false);
  useEffect(() => {
    pickedByUser.current = false;
  }, [selectedPizzaId]);
  useEffect(() => {
    if (pickedByUser.current) return;
    if (obstacleNok) {
      setActiveTab((tab) => {
        if (tab !== "obstacles") tabBeforeObstacle.current = tab;
        return "obstacles";
      });
      openedForObstacle.current = true;
    } else if (openedForObstacle.current) {
      setActiveTab(tabBeforeObstacle.current);
      openedForObstacle.current = false;
    }
  }, [selectedPizzaId, obstacleNok]);
  const selectTab = (tab: ViewerTab) => {
    pickedByUser.current = true;
    openedForObstacle.current = false;
    setActiveTab(tab);
  };
  // a layer opened from the evaluation panel counts as the user's pick
  useEffect(() => {
    if (!tabRequest) return;
    pickedByUser.current = true;
    openedForObstacle.current = false;
    setActiveTab(tabRequest.tab);
  }, [tabRequest]);
  useEffect(() => {
    if (!showObstacles) return;
    const onKey = (e: KeyboardEvent) => {
      const typing = e.target instanceof Element && e.target.closest("input, textarea, select, [contenteditable=true]");
      if (e.ctrlKey || e.metaKey || e.altKey || typing) return;
      if (e.key === "m" || e.key === "M") {
        e.preventDefault();
        setObstacleMode((m) => (m === "mask" ? "source" : "mask"));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showObstacles]);
  // The obstacle layer shows what the segmentation looked at: the whole pizza circle, also where an obstacle
  // hides the crust (the evaluation's pizza_roi is only the visible part; "Invalid Photo" has none at all).
  const paddedPizzaRoi = useMemo(
    () => (showObstacles && applyRoi ? (obstacleRoiFrame(obstacles) ?? pizzaRoi) : pizzaRoi),
    [showObstacles, applyRoi, obstacles, pizzaRoi],
  );

  // Get image assets using the same pattern as LayerRenderer
  const { src: maskedSrc } = useAsset({
    pizzaId: selectedPizzaId as string,
    type: "pizza_mask",
    enabled: !!selectedPizzaId,
  });

  const { src: heatmapSrc } = useAsset({
    pizzaId: selectedPizzaId as string,
    type: "heatmap",
    enabled: !!selectedPizzaId,
  });

  // Canvas refs
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hoveredClassCanvasRef = useRef<HTMLCanvasElement>(null);

  // Create RGBA image with mask as alpha channel using Canvas API
  const createRGBAWithMask = useCallback(
    async (
      originalImage: HTMLImageElement,
      maskSrc: string,
      roi?: { x: number; y: number; width: number; height: number },
    ): Promise<string> => {
      return new Promise((resolve, reject) => {
        const maskImg = new Image();
        maskImg.crossOrigin = "anonymous";

        maskImg.onload = () => {
          try {
            // Create canvas with appropriate dimensions
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d")!;

            canvas.width = roi ? roi.width : originalImage.width;
            canvas.height = roi ? roi.height : originalImage.height;

            // Draw the original image (cropped if ROI is provided)
            if (roi) {
              ctx.drawImage(
                originalImage,
                roi.x,
                roi.y,
                roi.width,
                roi.height,
                0,
                0,
                canvas.width,
                canvas.height,
              );
            } else {
              ctx.drawImage(originalImage, 0, 0, canvas.width, canvas.height);
            }

            // Get the image data to modify alpha channel
            const imageData = ctx.getImageData(
              0,
              0,
              canvas.width,
              canvas.height,
            );
            const data = imageData.data;

            // Create a temporary canvas for the mask
            const maskCanvas = document.createElement("canvas");
            const maskCtx = maskCanvas.getContext("2d")!;
            maskCanvas.width = canvas.width;
            maskCanvas.height = canvas.height;

            // Draw mask scaled to match image dimensions
            if (roi) {
              maskCtx.drawImage(
                maskImg,
                roi.x,
                roi.y,
                roi.width,
                roi.height,
                0,
                0,
                maskCanvas.width,
                maskCanvas.height,
              );
            } else {
              maskCtx.drawImage(
                maskImg,
                0,
                0,
                maskCanvas.width,
                maskCanvas.height,
              );
            }

            const maskData = maskCtx.getImageData(
              0,
              0,
              maskCanvas.width,
              maskCanvas.height,
            );

            // Apply mask to alpha channel
            for (let i = 0; i < data.length; i += 4) {
              const maskAlpha = maskData.data[i]; // Use red channel of mask as alpha
              data[i + 3] = maskAlpha; // Set alpha channel
            }

            // Put the modified image data back
            ctx.putImageData(imageData, 0, 0);

            // Return as data URL
            resolve(canvas.toDataURL("image/png"));
          } catch (error) {
            reject(error);
          }
        };

        maskImg.onerror = () => {
          reject(new Error("Failed to load mask image"));
        };

        maskImg.src = maskSrc;
      });
    },
    [],
  );

  // Draw canvas based on active tab
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !pizzaImage) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Use ROI if available, otherwise use full image
    const useRoi = paddedPizzaRoi !== null;

    if (useRoi) {
      canvas.width = paddedPizzaRoi.width;
      canvas.height = paddedPizzaRoi.height;

      // Clear canvas first
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw base image (cropped to ROI)
      ctx.drawImage(
        pizzaImage,
        paddedPizzaRoi.x,
        paddedPizzaRoi.y,
        paddedPizzaRoi.width,
        paddedPizzaRoi.height,
        0,
        0,
        canvas.width,
        canvas.height,
      );
    } else {
      // Fallback to full image if no ROI
      canvas.width = pizzaImage.width;
      canvas.height = pizzaImage.height;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(pizzaImage, 0, 0, pizzaImage.width, pizzaImage.height);
    }

    // Apply overlays based on tab (only for masked and evaluation tabs when ROI is available)
    if (useRoi) {
      // Apply overlays based on tab
      if (activeTab === "masked" && maskedSrc) {
        // Use Fabric.js to create RGBA image with mask as alpha channel
        createRGBAWithMask(pizzaImage, maskedSrc, paddedPizzaRoi)
          .then((dataURL) => {
            const maskedImg = new Image();
            maskedImg.onload = () => {
              ctx.clearRect(0, 0, canvas.width, canvas.height);
              ctx.drawImage(maskedImg, 0, 0, canvas.width, canvas.height);
            };
            maskedImg.src = dataURL;
          })
          .catch((error) => {
            console.error("Failed to create masked image:", error);
            // Fallback to original method
            const maskImg = new Image();
            maskImg.onload = () => {
              ctx.globalCompositeOperation = "screen";
              ctx.filter = "invert(100%)";
              ctx.globalAlpha = 0.8;
              ctx.drawImage(
                maskImg,
                paddedPizzaRoi.x,
                paddedPizzaRoi.y,
                paddedPizzaRoi.width,
                paddedPizzaRoi.height,
                0,
                0,
                paddedPizzaRoi.width,
                paddedPizzaRoi.height,
              );
              ctx.globalCompositeOperation = "source-over";
              ctx.filter = "none";
              ctx.globalAlpha = 1;
            };
            maskImg.src = maskedSrc;
          });
      } else if (activeTab === "evaluation") {
        // Apply mask first using Fabric.js, then heatmap
        if (maskedSrc) {
          createRGBAWithMask(pizzaImage, maskedSrc, paddedPizzaRoi)
            .then((dataURL) => {
              const maskedImg = new Image();
              maskedImg.onload = () => {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(maskedImg, 0, 0, canvas.width, canvas.height);

                // Apply heatmap overlay if available
                if (heatmapSrc) {
                  const heatmapImg = new Image();
                  heatmapImg.onload = () => {
                    ctx.globalAlpha = (layerOpacity.heatmap || 80) / 100;
                    ctx.drawImage(
                      heatmapImg,
                      paddedPizzaRoi.x,
                      paddedPizzaRoi.y,
                      paddedPizzaRoi.width,
                      paddedPizzaRoi.height,
                      0,
                      0,
                      paddedPizzaRoi.width,
                      paddedPizzaRoi.height,
                    );
                    ctx.globalAlpha = 1;
                  };
                  heatmapImg.src = heatmapSrc;
                }
              };
              maskedImg.src = dataURL;
            })
            .catch((error) => {
              console.error(
                "Failed to create masked image for evaluation:",
                error,
              );
              // Fallback to original method
              const maskImg = new Image();
              maskImg.onload = () => {
                ctx.globalCompositeOperation = "screen";
                ctx.filter = "invert(100%)";
                ctx.globalAlpha = 0.8;
                ctx.drawImage(
                  maskImg,
                  paddedPizzaRoi.x,
                  paddedPizzaRoi.y,
                  paddedPizzaRoi.width,
                  paddedPizzaRoi.height,
                  0,
                  0,
                  paddedPizzaRoi.width,
                  paddedPizzaRoi.height,
                );
                ctx.globalCompositeOperation = "source-over";
                ctx.filter = "none";
                ctx.globalAlpha = 1;

                // Apply heatmap overlay if available
                if (heatmapSrc) {
                  const heatmapImg = new Image();
                  heatmapImg.onload = () => {
                    ctx.globalAlpha = (layerOpacity.heatmap || 80) / 100;
                    ctx.drawImage(
                      heatmapImg,
                      paddedPizzaRoi.x,
                      paddedPizzaRoi.y,
                      paddedPizzaRoi.width,
                      paddedPizzaRoi.height,
                      0,
                      0,
                      paddedPizzaRoi.width,
                      paddedPizzaRoi.height,
                    );
                    ctx.globalAlpha = 1;
                  };
                  heatmapImg.src = heatmapSrc;
                }
              };
              maskImg.src = maskedSrc;
            });
        } else if (heatmapSrc) {
          // If only heatmap is available (no mask)
          const heatmapImg = new Image();
          heatmapImg.onload = () => {
            ctx.globalAlpha = (layerOpacity.heatmap || 80) / 100;
            ctx.drawImage(
              heatmapImg,
              paddedPizzaRoi.x,
              paddedPizzaRoi.y,
              paddedPizzaRoi.width,
              paddedPizzaRoi.height,
              0,
              0,
              paddedPizzaRoi.width,
              paddedPizzaRoi.height,
            );
            ctx.globalAlpha = 1;
          };
          heatmapImg.src = heatmapSrc;
        }
      }
    }
  }, [
    activeTab,
    pizzaImage,
    paddedPizzaRoi,
    maskedSrc,
    heatmapSrc,
    layerOpacity.heatmap,
    createRGBAWithMask,
  ]);

  // Draw detected components overlay (bounding boxes, or the sauce mask for a sauce chip)
  useEffect(() => {
    const hoveredCanvas = hoveredClassCanvasRef.current;
    if (!hoveredCanvas) return;
    const ctx = hoveredCanvas.getContext("2d");
    if (!ctx) return;
    // use the ROI if available, otherwise the full image
    const frame = paddedPizzaRoi
      ? paddedPizzaRoi
      : pizzaImage
        ? { x: 0, y: 0, width: pizzaImage.width, height: pizzaImage.height }
        : null;
    if (!frame) return;
    hoveredCanvas.width = frame.width;
    hoveredCanvas.height = frame.height;
    ctx.clearRect(0, 0, hoveredCanvas.width, hoveredCanvas.height);
    drawHoveredComponentOverlay(ctx, {
      hoveredClass,
      metadata,
      paddedRoi: frame,
      sauceMask,
      cacheKey: selectedPizzaId ?? "",
      hoveredHeatmap: hoveredHeatmapImage,
    });
  }, [activeTab, hoveredClass, hoveredHeatmapImage, metadata, paddedPizzaRoi, pizzaImage, sauceMask, selectedPizzaId]);

  // Frame of the photo canvas in source pixels (the slicing SVG uses it as its viewBox)
  const frame = useMemo(
    () =>
      paddedPizzaRoi
        ? paddedPizzaRoi
        : pizzaImage
          ? { x: 0, y: 0, width: pizzaImage.width, height: pizzaImage.height }
          : null,
    [paddedPizzaRoi, pizzaImage],
  );

  const aspectRatio = useMemo(() => {
    // the crop is not known before the evaluation: hold a square (most pizza crops are) instead of the
    // frame's 4:3 that would jump when the evaluation arrives
    if (applyRoi && (!metadata || !pizzaImage) && evaluating) {
      return 1;
    }
    // Use ROI dimensions if available, otherwise use full image
    if (paddedPizzaRoi) {
      return paddedPizzaRoi.width / paddedPizzaRoi.height;
    }
    if (pizzaImage) {
      return pizzaImage.width / pizzaImage.height;
    }
    return defaultWidth / defaultHeight;
  }, [paddedPizzaRoi, pizzaImage, applyRoi, metadata, evaluating]);

  const tabs: { value: ViewerTab; label: string; icon: React.ReactNode; badge?: React.ReactNode }[] = [
    { value: "original", label: t("detail.viewer.original"), icon: <ImageIcon className="h-3.5 w-3.5" /> },
    { value: "masked", label: t("detail.viewer.masked"), icon: <Shapes className="h-3.5 w-3.5" /> },
    { value: "evaluation", label: t("detail.doneness"), icon: <Flame className="h-3.5 w-3.5" /> },
    {
      value: "slicing",
      label: t("slicing.tab"),
      icon: <Scissors className="h-3.5 w-3.5" />,
      badge: slicing?.small_count ? (
        <span className="text-[10px] font-semibold tabular-nums text-amber-300/90">
          {slicing.small_count}
        </span>
      ) : null,
    },
    {
      value: "obstacles",
      label: t("obstacle.tab"),
      icon: <ScanSearch className="h-3.5 w-3.5" />,
      badge: obstacleNok ? <span className="h-1.5 w-1.5 rounded-full bg-red-500" /> : null,
    },
  ];

  return (
    <div ref={ref} className={cn("w-full overflow-hidden rounded-xl border bg-card", className)}>
      {/* Layer tabs + crop toggle */}
      <div className="flex items-center gap-1 border-b px-1.5 py-1.5">
        <div
          role="tablist"
          aria-label={t("detail.viewer.layers")}
          className="flex min-w-0 flex-1 gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {tabs.map((tab) => {
            const active = activeTab === tab.value;
            return (
              <button
                key={tab.value}
                type="button"
                role="tab"
                aria-selected={active}
                title={tab.label}
                onClick={() => selectTab(tab.value)}
                className={cn(
                  "flex h-8 shrink-0 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium transition-colors sm:text-sm",
                  active
                    ? "bg-secondary text-foreground shadow-sm ring-1 ring-inset ring-border"
                    : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
                )}
              >
                {tab.icon}
                {/* phones: the active tab is labelled, the others are icons (all five fit) */}
                <span className={cn(!active && "sr-only sm:not-sr-only")}>{tab.label}</span>
                {tab.badge}
              </button>
            );
          })}
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => setApplyRoi((prev) => !prev)}
              aria-pressed={!applyRoi}
              aria-label={applyRoi ? t("detail.viewer.showWholeFrame") : t("detail.viewer.cropToPizza")}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground aria-pressed:bg-secondary aria-pressed:text-foreground"
            >
              {applyRoi ? <Maximize2 className="h-4 w-4" /> : <Crop className="h-4 w-4" />}
            </button>
          </TooltipTrigger>
          <TooltipContent>
            {applyRoi ? t("detail.viewer.showWholeFrame") : t("detail.viewer.cropToPizza")}
          </TooltipContent>
        </Tooltip>
      </div>

      {/* Photo: as large as the column allows, never taller than maxImageHeight */}
      <div className="bg-black/60">
        <div
          className="relative mx-auto w-full"
          style={{
            aspectRatio,
            maxWidth: maxImageHeight ? `calc((${maxImageHeight}) * ${aspectRatio})` : undefined,
          }}
        >
          {pizzaImage ? (
            <>
              {/* Main layer canvas */}
              <canvas ref={canvasRef} className="block h-full w-full" style={{ aspectRatio }} />
              {/* Detected components overlay canvas */}
              <canvas
                ref={hoveredClassCanvasRef}
                className="pointer-events-none absolute inset-0 z-10 h-full w-full"
                style={{ aspectRatio }}
              />
              {/* Slicing layer: hatched slices, cuts, area labels (replays its animation per pizza) */}
              {showSlicing && slicing && frame && (
                <div
                  key={`${selectedPizzaId}-${slicing.version}-${slicing.slices?.length ?? 0}`}
                  className="pointer-events-none absolute inset-0 z-20"
                >
                  <SlicingOverlay
                    slicing={slicing}
                    frame={frame}
                    hovered={hoveredSlice}
                    onHover={setHoveredSlice}
                    mode={slicingMode}
                  />
                  {slicingMode === "source" && pizzaImage && (
                    <div className="pointer-events-auto absolute inset-0">
                      <SlicingLoupe image={pizzaImage} frame={frame} slicing={slicing} />
                    </div>
                  )}
                  <SlicingBadge slicing={slicing} />
                  <SlicingModeToggle mode={slicingMode} onChange={setSlicingMode} />
                </div>
              )}
              {/* Obstacle layer: masks, coverage, crust gap (replays its animation per pizza) */}
              {showObstacles && obstacles && frame && (
                <div
                  key={`${selectedPizzaId}-${obstacles.details?.segmentation?.version}-${obstaclesPreview}`}
                  className="pointer-events-none absolute inset-0 z-20"
                >
                  <ObstacleOverlay
                    check={obstacles}
                    frame={frame}
                    hovered={hoveredObstacle}
                    onHover={setHoveredObstacle}
                    mode={obstacleMode}
                  />
                  <ObstacleBadge check={obstacles} />
                  <ObstacleModeToggle mode={obstacleMode} onChange={setObstacleMode} />
                </div>
              )}
              {showObstacles && !obstacles && (obstaclesLoading || obstaclesError || !metadata) && (
                <div className="absolute inset-0 z-20 flex items-center justify-center bg-slate-950/30 backdrop-blur-[1px]">
                  <div className="obstacle-legend flex max-w-xs items-center gap-3 rounded-xl border border-white/15 bg-slate-950/75 px-4 py-3 text-sm text-white shadow-2xl">
                    {obstaclesLoading ? (
                      <Loader2 className="h-5 w-5 shrink-0 animate-spin opacity-80" />
                    ) : (
                      <ScanSearch className="h-5 w-5 shrink-0 opacity-80" />
                    )}
                    <span>{obstaclesLoading ? t("obstacle.loading") : t("obstacle.noData")}</span>
                  </div>
                </div>
              )}
              {showSlicing && !slicing && (slicingLoading || slicingError || !metadata) && (
                <div className="absolute inset-0 z-20 flex items-center justify-center bg-slate-950/30 backdrop-blur-[1px]">
                  <div className="slicing-legend flex max-w-xs items-center gap-3 rounded-xl border border-white/15 bg-slate-950/75 px-4 py-3 text-sm text-white shadow-2xl">
                    {slicingLoading ? (
                      <Loader2 className="h-5 w-5 shrink-0 animate-spin opacity-80" />
                    ) : (
                      <Scissors className="h-5 w-5 shrink-0 opacity-80" />
                    )}
                    <span>{slicingLoading ? t("slicing.loading") : t("slicing.noData")}</span>
                  </div>
                </div>
              )}
              {evaluating && (
                <div className="pointer-events-none absolute left-3 top-3 z-30 flex items-center gap-2 rounded-full border border-white/15 bg-slate-950/70 px-2.5 py-1 text-xs text-white shadow-xl backdrop-blur-md">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  {t("detail.evaluating")}
                </div>
              )}
            </>
          ) : selectedPizzaId ? (
            <div className="absolute inset-0 animate-pulse bg-muted/40" />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
              {t("detail.viewer.noPhoto")}
            </div>
          )}
        </div>
      </div>

      {/* Slicing: area of every slice against the target */}
      {showSlicing && slicing && (
        <div className="pb-4">
          <SlicingPanel slicing={slicing} hovered={hoveredSlice} onHover={setHoveredSlice} />
        </div>
      )}

      {/* Obstacles: covered share against the limit, what was found */}
      {showObstacles && obstacles && (
        <div className="pb-4">
          <ObstaclePanel
            key={`${selectedPizzaId}-${obstaclesPreview}`}
            check={obstacles}
            preview={obstaclesPreview}
            hovered={hoveredObstacle}
            onHover={setHoveredObstacle}
          />
        </div>
      )}
    </div>
  );
});

PizzaEvaluationVisualSimple.displayName = "PizzaEvaluationVisualSimple";

export default PizzaEvaluationVisualSimple;
